import React, { useMemo } from 'react';
import { CustomDynamicResultTable, ColGroup, TableHead, TableBody } from '@css-xp/base';
import { useQuery } from '@apollo/client';
import { fetchCloudSiteProductAppAndSubscriptions } from '../../../queries/fetch-cloud-site-products';
import { SubscriptionTabProps } from '../../../common-types';
import {
  FetchCloudSiteProductAppAndSubscriptionsQuery,
  FetchCloudSiteProductsV2QueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import { emptyStateProps } from '@css-xp/base/common-tabs';
import ProductGroupTable from './product-group-table';
import { columns } from './table-columns';
import capitalize from 'lodash/capitalize';
import './subscription-tab.scss';

const SubscriptionTabWithGroupedProducts = (props: SubscriptionTabProps) => {
  const { parentSen, cloudSiteId } = props;
  const queryVarsForGroupedProductAndApps = {
    parentSen: parentSen,
    cloudSiteId: cloudSiteId,
    showCcpData: true,
  };

  const { error: groupedProductDataError, loading: groupedProductDataLoading, data: groupedProductData } = useQuery<
    FetchCloudSiteProductAppAndSubscriptionsQuery,
    FetchCloudSiteProductsV2QueryVariables
  >(fetchCloudSiteProductAppAndSubscriptions, {
    variables: queryVarsForGroupedProductAndApps,
    displayName: 'fetchCloudSiteProducts',
    nextFetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const productsAppsAndSubscriptions = useMemo(() => groupedProductData?.cloudSiteProductsAppsAndSubscriptions || {}, [
    groupedProductData?.cloudSiteProductsAppsAndSubscriptions,
  ]);

  const isEmpty = useMemo(() => {
    if (groupedProductDataLoading || groupedProductDataError) return true;

    const { jira = {}, confluence = {}, statuspage = {}, other = {} } = productsAppsAndSubscriptions as Record<
      string,
      any
    >;

    const totalJiraProducts = jira.products?.length || 0;
    const totalConfluenceProducts = confluence.products?.length || 0;
    const totalStatuspageProducts = statuspage.products?.length || 0;
    const totalOtherProducts = other.products?.length || 0;

    const totalProducts = totalJiraProducts + totalConfluenceProducts + totalStatuspageProducts + totalOtherProducts;

    return totalProducts === 0;
  }, [groupedProductDataError, groupedProductDataLoading, productsAppsAndSubscriptions]);

  const getHeader = (key: string) => {
    return (
      <div className="product-header">
        {['jira', 'confluence'].includes(key) ? `${capitalize(key)} family` : 'Other products'}
      </div>
    );
  };

  return (
    <div id="cloud-site-products" data-testid="cloud-site-products" className="cloud-site-subscriptions-tab">
      <CustomDynamicResultTable
        className="cloudsite-linked-subscriptions-table"
        testId="cloud-site-products"
        heading="Subscriptions"
        isLoading={groupedProductDataLoading}
        error={groupedProductDataError?.toString()}
        noResults={isEmpty}
        boxShadow={true}
        emptyStateProps={emptyStateProps}
        isPaginationEnabled={false}
      >
        <ColGroup columns={columns} />
        <TableHead columns={columns} />

        <TableBody>
          {!groupedProductDataLoading && (
            <tr>
              <td colSpan={columns.length}>
                <div className="table-grey-background">
                  {Object.keys(productsAppsAndSubscriptions).map((entitlementName: string, index: number) => {
                    // @ts-ignore
                    const { products, apps } = productsAppsAndSubscriptions[entitlementName];
                    const isGroupEmpty = !products?.length;
                    if (!!products?.length || !!apps?.length) {
                      return (
                        <div className="product-app-family-wrapper" key={`${entitlementName}-${index}`}>
                          <ProductGroupTable products={products} heading={getHeader(entitlementName)} />
                          <ProductGroupTable products={apps} heading={`Apps for ${capitalize(entitlementName)}`} />

                          {!isGroupEmpty && <div className="seperator" />}
                        </div>
                      );
                    } else return null;
                  })}
                </div>
              </td>
            </tr>
          )}
        </TableBody>
      </CustomDynamicResultTable>
    </div>
  );
};

export default React.memo(SubscriptionTabWithGroupedProducts);
